import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import moment from 'moment';

import { ReactComponent as EmptySlotCalendar } from 'assets/icons/empty-slot-calendar.svg';
import { Divider } from 'components/DentalDivider';
import {
  PRACTITIONER_SEARCH_TITLE,
  PRACTITIONER_SELECT_PROVIDER_TITLE,
} from 'utils/constants/common';
import useIsMobile from 'hooks/useIsMobile';
import DayViewNavigation from 'components/DayViewNavigation/DayViewNavigation';
import MonthViewNavigation from 'components/MonthViewNavigation/MonthViewNavigation';
import DayView from 'components/DayView/DayView';
import PractitionerDropdownSection from 'components/PractitionerDropdownSection/PractitionerDropdownSection';
import ReasonDropdownSection from 'components/ReasonDropdownSection/ReasonDropdownSection';
import { ITimeSlot } from 'interfaces/timeslotType';
import { getPractitionerOptions } from 'services/APIs/getAllClinicsPractitioners';
import {
  getClinicAvailability,
  IWeeklyTimeSlotsClinicData,
} from 'services/APIs/getClinicAvailability';
import { getPractitionerAvailability } from 'services/APIs/getPractitionerAvailability';

import css from './SingleBooking.module.scss';
import { getFilterDates } from 'utils/dates';
import { parseQueryUrl } from 'utils/parseQueryUrl';
import { getSlotsPer30MinutesForAvailability } from 'utils/updatedSlotsPer30Minutes';
import MonthViewBookNowPanel from 'components/MonthViewBookNowPanel/MonthViewBookNowPanel';
import { IClinicData } from 'redux/clinicDetailsSlice';
import { useAppSelector, useAppDispatch } from 'redux/reduxHooks';
import { setShowInsuranceModal } from 'redux/clinicDetailsSlice';
import InsuranceSelectionModal from '../InsuranceSelectionModal/InsuranceSelectionModal';
import {
  IClinicBookingData,
  IPractitionerBookingData,
} from 'pages/ClinicDetailsPage/ClinicDetailsPage';
import { ModeOption } from '../utils/constants';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import BookingModeSelector from '../BookingModeSelector/BookingModeSelector';
import { IClinicServicesOption } from '../BookNowPanel';

import HeaderSection from '../HeaderSection/HeaderSection';
import axios from 'axios';
import { PATIENT_EXAM_AND_CLEANING } from 'components/DentalMap/utils/constant';
import { renderToast } from 'components/CustomToast/CustomToast';
import EnhancedPractitionerDropdown from 'components/EnhancedPractitionerDropdown/EnhancedPractitionerDropdown';
import { Specialty } from 'interfaces/specialty';
import {
  findSuitablePractitionerForExamAndCleaningService,
  findSuitablePractitionerForNormalService,
} from '../utils/findPractitioner';

interface Props {
  clinicData: IClinicData;
  clinicServicesOptions: IClinicServicesOption[];
  onClickClinicTimeSlot: (data: IClinicBookingData) => void;
  onClickPractitionerTimeSlot: (data: IPractitionerBookingData) => void;
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  mode: ModeOption;
  setMode: React.Dispatch<React.SetStateAction<ModeOption>>;
}

export interface IPractitionerOptions {
  id: string;
  title: string;
  avatar: string;
  specialties: Specialty[];
  rating: number;
  reviewCount: number;
  insurance?: {
    id: string;
    doctorId: string;
    isInNetwork: boolean;
  } | null;
}

const initialPractitionerValue = {
  id: 'all',
  title: PRACTITIONER_SEARCH_TITLE,
};

const SingleBooking: React.FC<Props> = ({
  clinicData: {
    id: clinicId,
    timezone: clinicTimezone,
    name,
    slotInterval,
    isDoctorSelectionEnabled,
    isPreferredPractitionerEnabled,
  },
  clinicServicesOptions,
  onClickClinicTimeSlot,
  onClickPractitionerTimeSlot,
  date,
  setDate,
  mode,
  setMode,
}) => {
  const initialLoadingState = useRef(true);

  const location = useLocation();
  const { serviceSlugNameUrl, codeUrl } = parseQueryUrl(location);

  const selectedLastInsurance = useAppSelector(
    (state) => state.bookingFormSlice.selectedLastInsurance
  );

  const lastInsuranceCarrierId = selectedLastInsurance?.insuranceCarrierId;

  const showInsuranceModal = useAppSelector(
    (state) => state.clinicDetailsSlice.clinicData.showInsuranceModal
  );

  const dispatch = useAppDispatch();

  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const isRwgTokenInUrl = location.search.includes('rwg_token');

  const containerClassname = cx({
    [css['container']]: true,
    [css['container-iframe']]: isLoadedInsideIframe,
  });

  const [practitionerOptions, setPractitionerOptions] = useState<
    IPractitionerOptions[]
  >([]);

  const [isPractitionerOptionsLoading, setIsPractitionerOptionsLoading] =
    useState(true);

  const [dentistOptions, setDentistOptions] = useState<IPractitionerOptions[]>(
    []
  );

  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);

  const [service, setService] = useState(() => {
    const defaultServiceOption = clinicServicesOptions[0];

    if (!serviceSlugNameUrl) {
      return defaultServiceOption;
    }

    const service = clinicServicesOptions.find(
      (service) => service.slug === serviceSlugNameUrl
    );

    if (!service && !isRwgTokenInUrl) {
      renderToast({
        message: `The reason you are trying to book an appointment for is currently not available. Please choose another reason or contact ${name} for more information.`,
        type: 'error',
      });
    }

    return service ?? defaultServiceOption;
  });

  const [practitioner, setPractitioner] = useState(initialPractitionerValue);
  const [dentist, setDentist] = useState(initialPractitionerValue);

  const [isPractitionerDropdownOpen, setIsPractitionerDropdownOpen] =
    useState(false);

  const [clinicData, setClinicData] = useState<IWeeklyTimeSlotsClinicData>(
    {} as IWeeklyTimeSlotsClinicData
  );

  // For  selected practitioner
  const [practitionerData, setPractitionerData] = useState<{
    availableBlocks: ITimeSlot[];
    doctorInsurances: {
      insuranceCarrierId: string;
      isInNetwork: boolean;
    }[];
  }>({
    availableBlocks: [],
    doctorInsurances: [],
  });

  const [isClinicTimeSlotLoading, setIsClinicTimeSlotLoading] = useState(true);
  const [isPractitionerTimeSlotLoading, setIsPractitionerTimeSlotLoading] =
    useState(false);
  const [isDayView, setIsDayView] = useState(true);

  const isMobile = useIsMobile();

  const isDoctorDisplayedForExamCleaning =
    isDoctorSelectionEnabled && service.isExamAndCleaningService;

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    const fetchPractitionerOptions = async () => {
      const sortBy = 'specialist';
      const practitionerOptions = await getPractitionerOptions({
        clinicId: clinicId,
        serviceId: service.id,
        sortBy,
        cancelToken: source.token,
        code: codeUrl,
        serviceSlugName: serviceSlugNameUrl,
        ...(lastInsuranceCarrierId && { insuranceId: lastInsuranceCarrierId }),
      });

      setPractitionerOptions(practitionerOptions);
      setIsPractitionerOptionsLoading(false);
    };

    const fetchDentistOptions = async () => {
      const shouldFetchSplitSchedulingDoctors = true;
      const sortBy = 'specialist';
      const dentistOptions = await getPractitionerOptions({
        clinicId,
        serviceId: service.id,
        sortBy,
        cancelToken: source.token,
        shouldFetchSplitSchedulingDoctors,
        code: codeUrl,
        serviceSlugName: serviceSlugNameUrl,
        ...(lastInsuranceCarrierId && { insuranceId: lastInsuranceCarrierId }),
      });

      setDentistOptions(dentistOptions);
    };

    if (isDoctorDisplayedForExamCleaning && isPreferredPractitionerEnabled) {
      fetchDentistOptions();
    } else {
      setDentistOptions([]);
    }

    fetchPractitionerOptions();

    return () => {
      source.cancel();
    };
  }, [
    clinicId,
    codeUrl,
    isDoctorDisplayedForExamCleaning,
    isPreferredPractitionerEnabled,
    service.id,
    service.title,
    serviceSlugNameUrl,
    lastInsuranceCarrierId,
  ]);

  useEffect(() => {
    if (!isDayView || isPractitionerOptionsLoading) {
      return;
    }

    if (
      !isPractitionerOptionsLoading &&
      practitionerOptions.length === 0 &&
      lastInsuranceCarrierId
    ) {
      setIsClinicTimeSlotLoading(false);
      setIsPractitionerTimeSlotLoading(false);
      return;
    }

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    const fetchClinicAvailability = async () => {
      setIsClinicTimeSlotLoading(true);
      const res = await getClinicAvailability({
        clinicId,
        dates: getFilterDates(date),
        serviceId: service.id,
        timezone: clinicTimezone,
        timeBlocks: [0],
        ...(codeUrl && { code: codeUrl }),
        ...(serviceSlugNameUrl && { serviceSlugName: serviceSlugNameUrl }),
        ...(dentist.id !== initialPractitionerValue.id && {
          dentistId: dentist.id,
        }),
        ...(lastInsuranceCarrierId && { insuranceId: lastInsuranceCarrierId }),
        cancelToken: source.token,
      });
      setClinicData(res);
      setIsClinicTimeSlotLoading(false);
    };

    const fetchPractitionerAvailability = async () => {
      setIsPractitionerTimeSlotLoading(true);
      const res = await getPractitionerAvailability({
        clinicId: clinicId,
        dates: getFilterDates(date),
        practitionerId: practitioner.id,
        serviceId: service.id,
        timezone: clinicTimezone,
        timeBlocks: [0],
        ...(codeUrl && { code: codeUrl }),
        ...(serviceSlugNameUrl && { serviceSlugName: serviceSlugNameUrl }),
        ...(dentist.id !== initialPractitionerValue.id && {
          dentistId: dentist.id,
        }),
        ...(lastInsuranceCarrierId && { insuranceId: lastInsuranceCarrierId }),
        cancelToken: source.token,
      });
      setPractitionerData(res);
      setIsPractitionerTimeSlotLoading(false);
    };

    if (practitioner.id !== 'all') {
      fetchPractitionerAvailability();
    } else {
      fetchClinicAvailability();
    }

    return () => {
      source.cancel();
    };
  }, [
    isDayView,
    clinicId,
    clinicTimezone,
    codeUrl,
    date,
    practitioner.id,
    service.id,
    serviceSlugNameUrl,
    dentist.id,
    lastInsuranceCarrierId,
    isPractitionerOptionsLoading,
    practitionerOptions.length,
  ]);

  const toggleOpenServiceDropdown = (value: boolean) =>
    setIsServiceDropdownOpen(value);

  const toggleOpenPractitionerDropdown = (value: boolean) =>
    setIsPractitionerDropdownOpen(value);

  const setLoadingState = () => {
    isClinicSearch
      ? setIsClinicTimeSlotLoading(true)
      : setIsPractitionerTimeSlotLoading(true);
  };

  const handleChangeServiceOption = (option: IClinicServicesOption) => {
    if (!service.id.includes(PATIENT_EXAM_AND_CLEANING)) {
      setDentist(initialPractitionerValue);
    }
    if (option.id !== service.id) {
      setLoadingState();
      setPractitioner(initialPractitionerValue);
      setDate(moment().toDate().toDateString());
    }
    setService(option);
  };

  const handleChangePractitionerOption = (option: {
    id: string;
    title: string;
  }) => {
    if (option.id === practitioner.id) {
      return;
    }

    if (option.id === initialPractitionerValue.id) {
      setIsClinicTimeSlotLoading(true);
    }

    setIsPractitionerTimeSlotLoading(true);
    setPractitioner(option);
  };

  const clickClinicTimeSlot = (
    timeSlot: number,
    appointmentDate: string,
    practitionerIds: string[]
  ) => {
    const isExamCleaningService = service.isExamAndCleaningService;

    if (!isExamCleaningService) {
      const result = findSuitablePractitionerForNormalService({
        appointmentDate,
        timeSlot,
        serviceDuration: service.duration,
        availableDoctorsFromSearchAPI: clinicData.doctors.map((item) => ({
          id: item.id,
          title: item.title,
          doctorInsurances: item.doctorInsurances,
          availableBlocks: item.availableBlocks,
        })),
      });

      if (!result) {
        return;
      }

      onClickClinicTimeSlot({
        serviceId: service.id,
        appointmentDate,
        timeSlot,
        operatoryId: result.operatoryId,
        practitioner: {
          id: result.practitioner.id,
          title: result.practitioner.title,
          isChosen: false,
        },
        dentist: {
          id: null,
          timeSlot: timeSlot,
          timeSlotType: null,
        },
        isExamCleaningService: service.isExamAndCleaningService,
        insurancesByAvailableDoctors: clinicData.doctors.map((d) => ({
          id: d.id,
          doctorInsurances: d.doctorInsurances,
          availableBlocks: (d.availableBlocks || [])
            .filter((block) => block.date === appointmentDate)
            .map((block) => ({
              date: block.date,
              isSplitScheduling: block.searchingService?.isSplitScheduling,
              dentists: (block.dentists || []).map((dentist) => ({
                dentistId: dentist.dentistId,
                headAvailableBlockNumbers: dentist.headAvailableBlockNumbers,
                tailAvailableBlockNumbers: dentist.tailAvailableBlockNumbers,
                doctorInsurances: dentist.doctorInsurances,
              })),
            })),
        })),
      });

      return;
    } else {
      const result = findSuitablePractitionerForExamAndCleaningService({
        appointmentDate,
        timeSlot,
        allDoctorOptions: practitionerOptions,
        selectedHygienistId: null,
        doctorIdsWithMatchingSelectedTimeSlot: practitionerIds,
        dentist,
        availableDoctorsFromSearchAPI: clinicData.doctors,
      });

      if (result) {
        onClickClinicTimeSlot({
          serviceId: service.id,
          appointmentDate,
          timeSlot,
          operatoryId: result.operatoryId,
          practitioner: {
            id: result.practitioner.id,
            title: result.practitioner.title,
            isChosen: false,
          },
          dentist: result.dentistInfo,
          isExamCleaningService: service.isExamAndCleaningService,
          chairOptimizations: result.chairOptimizations,
          insurancesByAvailableDoctors: (clinicData.doctors || []).map((d) => ({
            id: d.id,
            doctorInsurances: d.doctorInsurances,
            availableBlocks: (d.availableBlocks || [])
              .filter((block) => block.date === appointmentDate)
              .map((block) => ({
                date: block.date,
                isSplitScheduling: block.searchingService?.isSplitScheduling,
                dentists: (block.dentists || []).map((dentist) => ({
                  dentistId: dentist.dentistId,
                  headAvailableBlockNumbers: dentist.headAvailableBlockNumbers,
                  tailAvailableBlockNumbers: dentist.tailAvailableBlockNumbers,
                  doctorInsurances: dentist.doctorInsurances,
                })),
              })),
          })),
        });
      }
    }
  };

  const clickPractitionerTimeSlot = (
    timeSlot: number,
    appointmentDate: string
  ) => {
    const isExamCleaningService = service.isExamAndCleaningService;

    if (!isExamCleaningService) {
      const result = findSuitablePractitionerForNormalService({
        appointmentDate,
        timeSlot,
        serviceDuration: service.duration,
        availableDoctorsFromSearchAPI: [
          {
            id: practitioner.id,
            title: practitioner.title,
            doctorInsurances: [],
            availableBlocks: practitionerData.availableBlocks.map((item) => ({
              date: item.date,
              availableBlockOperatories: item.availableBlockOperatories,
              blocks: item.blocks,
              searchingService: item.searchingService,
              isOptimizedApplied: item.isOptimizedApplied,
            })),
          },
        ],
      });

      if (!result) {
        return;
      }

      onClickPractitionerTimeSlot({
        serviceId: service.id,
        appointmentDate,
        timeSlot,
        operatoryId: result.operatoryId,
        practitioner: {
          id: result.practitioner.id,
          title: result.practitioner.title,
          isChosen: true,
        },
        dentist: {
          id: null,
          timeSlot: timeSlot,
          timeSlotType: null,
        },
        isExamCleaningService: service.isExamAndCleaningService,
        insurancesByAvailableDoctors: [
          {
            id: practitioner.id,
            doctorInsurances: practitionerData.doctorInsurances,
            availableBlocks: (practitionerData.availableBlocks || [])
              .filter((block) => block.date === appointmentDate)
              .map((block: any) => ({
                date: block.date,
                isSplitScheduling: block.searchingService?.isSplitScheduling,
                dentists: (block.dentists || []).map((dentist: any) => ({
                  dentistId: dentist.dentistId,
                  headAvailableBlockNumbers: dentist.headAvailableBlockNumbers,
                  tailAvailableBlockNumbers: dentist.tailAvailableBlockNumbers,
                  doctorInsurances: dentist.doctorInsurances,
                })),
              })),
          },
        ],
      });

      return;
    } else {
      const result = findSuitablePractitionerForExamAndCleaningService({
        appointmentDate,
        timeSlot,
        allDoctorOptions: practitionerOptions,
        selectedHygienistId: practitioner.id,
        doctorIdsWithMatchingSelectedTimeSlot: [practitioner.id],
        dentist,
        availableDoctorsFromSearchAPI: [
          {
            id: practitioner.id,
            title: practitioner.title,
            doctorInsurances: [],
            availableBlocks: practitionerData.availableBlocks.map((item) => ({
              date: item.date,
              availableBlockOperatories: item.availableBlockOperatories,
              blocks: item.blocks,
              searchingService: item.searchingService,
              isOptimizedApplied: item.isOptimizedApplied,
            })),
          },
        ],
      });

      if (result) {
        onClickPractitionerTimeSlot({
          serviceId: service.id,
          appointmentDate,
          timeSlot,
          operatoryId: result.operatoryId,
          practitioner: {
            id: result.practitioner.id,
            title: result.practitioner.title,
            isChosen: true,
          },
          dentist: result.dentistInfo,
          isExamCleaningService: service.isExamAndCleaningService,
          chairOptimizations: result.chairOptimizations,
          insurancesByAvailableDoctors: [
            {
              id: practitioner.id,
              doctorInsurances: practitionerData.doctorInsurances,
              availableBlocks: (practitionerData.availableBlocks || [])
                .filter((block) => block.date === appointmentDate)
                .map((block: any) => ({
                  date: block.date,
                  isSplitScheduling: block.searchingService?.isSplitScheduling,
                  dentists: (block.dentists || []).map((dentist: any) => ({
                    dentistId: dentist.dentistId,
                    headAvailableBlockNumbers:
                      dentist.headAvailableBlockNumbers,
                    tailAvailableBlockNumbers:
                      dentist.tailAvailableBlockNumbers,
                    doctorInsurances: dentist.doctorInsurances,
                  })),
                })),
            },
          ],
        });
      }
    }
  };

  const handleDateUpdate = (newDate: string) => {
    initialLoadingState.current = false;
    setLoadingState();
    setDate(newDate);
  };

  const onNavigateToDayView = (dateStr: string) => {
    const parsedDate = moment(dateStr, 'YYYY-MM-DD').toDate().toDateString();
    setDate(parsedDate);
    setIsDayView(true);
  };

  const parsedDate = useMemo(() => {
    if (date && !moment(date, 'YYYY-MM-DD').isValid()) {
      return moment(Date.parse(date)).format('YYYY-MM-DD');
    }
    return date;
  }, [date]);

  const isClinicSearch = practitioner.id === 'all';

  const updatedSlotsPer30MinutesForPractitioner =
    getSlotsPer30MinutesForAvailability(
      practitionerData.availableBlocks,
      slotInterval
    );

  const updatedSlotsPer30MinutesForPractice =
    clinicData?.doctors?.map((doctor) => ({
      ...doctor,
      availableBlocks: getSlotsPer30MinutesForAvailability(
        doctor.availableBlocks as ITimeSlot[],
        slotInterval
      ),
    })) ?? [];

  const updatedClinicData = {
    ...clinicData,
    doctors: updatedSlotsPer30MinutesForPractice,
  } as IWeeklyTimeSlotsClinicData;

  const isEmptySlotForPractitioner =
    !updatedSlotsPer30MinutesForPractitioner ||
    updatedSlotsPer30MinutesForPractitioner.length === 0;

  const isEmptySlotForClinic = updatedClinicData?.doctors?.every((doctor) =>
    doctor.availableBlocks.every((block) => block.blocks.length === 0)
  );

  const isInitialEmptySlot =
    initialLoadingState.current &&
    (isEmptySlotForPractitioner || isEmptySlotForClinic);

  const onChangePractitioner = (
    isHygienistTab: boolean,
    practitionerId: string
  ) => {
    initialLoadingState.current = false;

    const isSameOptionClicked = isHygienistTab
      ? practitioner.id === practitionerId
      : dentist.id === practitionerId;

    if (isSameOptionClicked) {
      return;
    }

    setLoadingState();
    setIsPractitionerTimeSlotLoading(true);

    if (isHygienistTab) {
      const practitioner = practitionerOptions.find(
        (practitioner) => practitioner.id === practitionerId
      );
      setPractitioner(practitioner || initialPractitionerValue);
    } else {
      const dentist = dentistOptions.find(
        (doctor) => doctor.id === practitionerId
      );
      setDentist(dentist || initialPractitionerValue);
    }
  };

  const isEnhancedPractitionerDropdownDisplayed =
    isDoctorDisplayedForExamCleaning && dentistOptions.length > 0;

  return (
    <div className={containerClassname}>
      <HeaderSection
        title={isLoadedInsideIframe ? 'Schedule Online' : 'Select Time '}
      >
        <BookingModeSelector value={mode} onChange={setMode} />
      </HeaderSection>
      <div className={css['section-container']}>
        <ReasonDropdownSection
          isOpen={isServiceDropdownOpen}
          options={clinicServicesOptions}
          optionValue={service}
          onChangeIsOpen={toggleOpenServiceDropdown}
          onChangeServiceOption={handleChangeServiceOption}
          isOneDropdownFullWidth={!isPreferredPractitionerEnabled}
        />
        {isPreferredPractitionerEnabled &&
          (isEnhancedPractitionerDropdownDisplayed ? (
            <EnhancedPractitionerDropdown
              practitionerOptions={practitionerOptions}
              dentistOptions={dentistOptions}
              onChange={onChangePractitioner}
              currentPractitioner={practitioner}
              currentDentist={dentist}
            />
          ) : (
            <PractitionerDropdownSection
              isOpen={isPractitionerDropdownOpen}
              options={practitionerOptions}
              optionValue={
                practitioner.id === 'all'
                  ? {
                      ...practitioner,
                      title: PRACTITIONER_SELECT_PROVIDER_TITLE,
                    }
                  : practitioner
              }
              onChangeIsOpen={toggleOpenPractitionerDropdown}
              onChangePractitionerOption={handleChangePractitionerOption}
              defaultOption={
                initialPractitionerValue.id === 'all'
                  ? {
                      ...initialPractitionerValue,
                      title: PRACTITIONER_SELECT_PROVIDER_TITLE,
                    }
                  : initialPractitionerValue
              }
            />
          ))}
      </div>
      <div className={css['navigation-container']}>
        {isDayView ? (
          <DayViewNavigation date={date} onDateUpdate={handleDateUpdate} />
        ) : (
          <MonthViewNavigation date={parsedDate} setDate={handleDateUpdate} />
        )}
        <div className={css['navigation-section']}>
          <button
            className={`${css['navigation-btn']} ${
              !isDayView && css['navigation-btn-inactive']
            }`}
            onClick={() => setIsDayView(true)}
          >
            {isMobile ? 'Day' : 'Day View'}
          </button>
          <button
            className={`${css['navigation-btn']} ${
              isDayView && css['navigation-btn-inactive']
            }`}
            onClick={() => setIsDayView(false)}
          >
            {isMobile ? 'Month' : 'Month View'}
          </button>
        </div>
      </div>
      <Divider orientation="horizontal" className={css['divider']} />
      {!isPractitionerOptionsLoading &&
      practitionerOptions.length === 0 &&
      lastInsuranceCarrierId ? (
        <div className={css['no-practitioners']}>
          <EmptySlotCalendar />
          <span>No practitioners available</span>
        </div>
      ) : isDayView ? (
        <DayView
          key={`${practitioner.id}-${service.id}-${dentist.id}-${date}`}
          isLoading={
            isClinicSearch
              ? isClinicTimeSlotLoading
              : isPractitionerTimeSlotLoading
          }
          date={date}
          onDateUpdate={handleDateUpdate}
          isClinicSearch={isClinicSearch}
          clinicData={updatedClinicData}
          timeSlots={updatedSlotsPer30MinutesForPractitioner}
          onClickClinicTimeSlot={clickClinicTimeSlot}
          onClickPractitionerTimeSlot={clickPractitionerTimeSlot}
          clinicId={clinicId}
          practitionerId={
            practitioner.id !== 'all' ? practitioner.id : undefined
          }
          serviceId={service.id}
          isInitialEmptySlot={isInitialEmptySlot}
          dentistId={
            dentist.id !== initialPractitionerValue.id ? dentist.id : ''
          }
        />
      ) : (
        <div
          className={
            isLoadedInsideIframe ? css['month-view-section-iframe'] : ''
          }
        >
          <MonthViewBookNowPanel
            isLoading={
              isClinicSearch
                ? isClinicTimeSlotLoading
                : isPractitionerTimeSlotLoading
            }
            setIsLoading={(isLoading: boolean) => {
              setIsClinicTimeSlotLoading(isLoading);
              setIsPractitionerTimeSlotLoading(isLoading);
            }}
            clinicId={clinicId}
            serviceId={service.id}
            practitionerId={practitioner.id !== 'all' ? practitioner.id : null}
            date={parsedDate}
            clinicTimezone={clinicTimezone}
            onNavigateToDayView={onNavigateToDayView}
            slotInterval={slotInterval}
            dentistId={
              dentist.id !== initialPractitionerValue.id ? dentist.id : ''
            }
          />
        </div>
      )}
      <InsuranceSelectionModal
        clinicId={clinicId}
        open={!!showInsuranceModal}
        onClose={() => dispatch(setShowInsuranceModal(false))}
      />
    </div>
  );
};

export default SingleBooking;
